/**
 * AdminCmsPageEditPage — Block-based page editor
 *
 * /admin/cms-pages/:id
 *
 * Layout:
 *   - Left: blocks list (drag-reorder, delete) + "Add block" buttons
 *   - Center: live preview (renders via BlockRenderer with selected block highlighted)
 *   - Right: edit panel for selected block's props + page settings panel
 */
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Eye, Trash2, ArrowUp, ArrowDown, Plus, Settings, Layers, ExternalLink } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import BlockRenderer from "@/components/BlockRenderer";

// Default props per block type
const BLOCK_DEFAULTS = {
    hero: { title: "Hero Başlık", subtitle: "Alt başlık", bg_image_url: "", cta_label: "", cta_url: "", overlay_opacity: 0.5, align: "center", min_height: "480px", eyebrow: "" },
    text: { content_html: "<p>Buraya metin girin...</p>" },
    image: { url: "", alt: "", caption: "", link: "", full_width: false },
    gallery: { title: "", images: [], columns: 3 },
    cta: { title: "Hadi Başlayalım", subtitle: "", primary_label: "Rezervasyon Yap", primary_url: "/search", secondary_label: "", secondary_url: "", bg_color: "linear-gradient(135deg, #0ea5e9, #0369a1)" },
    faq: { title: "Sıkça Sorulan Sorular", items: [{ question: "Soru?", answer: "Cevap..." }] },
    embed_video: { title: "", youtube_url: "" },
    custom_html: { html: "<div>Özel HTML buraya...</div>" },
    hotel_list: { title: "Otellerimiz", source: "featured", destination_slug: "", limit: 6, columns: 3 },
    spacer: { height: "60px" },
};

const BLOCK_TYPE_LABELS = {
    hero: "Hero Banner",
    text: "Metin",
    image: "Tek Görsel",
    gallery: "Galeri",
    cta: "CTA / Çağrı",
    faq: "SSS / Akordeon",
    embed_video: "Video Gömme",
    custom_html: "Özel HTML",
    hotel_list: "Otel Listesi",
    spacer: "Boşluk",
};

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm";

export default function AdminCmsPageEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth() || {};
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [view, setView] = useState("blocks"); // 'blocks' | 'settings'

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        api.get(`/admin/cms/pages/${id}`).then((r) => {
            setPage(r.data);
            if ((r.data.blocks || []).length > 0) setSelectedBlockId(r.data.blocks[0].id);
        }).catch(() => setPage(null)).finally(() => setLoading(false));
    }, [id, user, authLoading]); // eslint-disable-line

    const save = async (extra = {}) => {
        setSaving(true); setMsg("");
        try {
            const payload = { ...page, ...extra };
            const r = await api.put(`/admin/cms/pages/${id}`, payload);
            setPage(r.data);
            setMsg("✓ Kaydedildi");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setMsg(e.response?.data?.detail || "Kayıt başarısız");
        } finally { setSaving(false); }
    };

    const publish = () => save({ status: "published" });
    const draft = () => save({ status: "draft" });

    if (loading) return <AdminLayout title="Sayfa Düzenle"><div>Yükleniyor...</div></AdminLayout>;
    if (!page) return <AdminLayout title="Sayfa Düzenle"><div className="text-red-500">Sayfa bulunamadı.</div></AdminLayout>;

    // Block ops
    const addBlock = (type) => {
        const newBlock = { id: `b_${Math.random().toString(36).slice(2, 10)}`, type, props: { ...BLOCK_DEFAULTS[type] } };
        setPage({ ...page, blocks: [...(page.blocks || []), newBlock] });
        setSelectedBlockId(newBlock.id);
    };
    const updateBlock = (blockId, propsPatch) => {
        setPage({ ...page, blocks: (page.blocks || []).map((b) => b.id === blockId ? { ...b, props: { ...b.props, ...propsPatch } } : b) });
    };
    const removeBlock = (blockId) => {
        if (!window.confirm("Bu bloğu silmek istediğinize emin misiniz?")) return;
        setPage({ ...page, blocks: (page.blocks || []).filter((b) => b.id !== blockId) });
        if (selectedBlockId === blockId) setSelectedBlockId(null);
    };
    const moveBlock = (blockId, dir) => {
        const arr = [...(page.blocks || [])];
        const idx = arr.findIndex((b) => b.id === blockId);
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        setPage({ ...page, blocks: arr });
    };

    const selectedBlock = (page.blocks || []).find((b) => b.id === selectedBlockId);

    return (
        <AdminLayout
            title={page.title || "Sayfa Düzenle"}
            action={
                <div className="flex items-center gap-2">
                    {msg && <span className="text-xs font-semibold text-emerald-600">{msg}</span>}
                    <Link to="/admin/cms-pages" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-sky-500"><ArrowLeft className="h-3.5 w-3.5" /> Sayfalar</Link>
                    {page.status === "published" && (
                        <Link to={`/p/${page.slug}`} target="_blank" className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold"><ExternalLink className="h-3.5 w-3.5" /> Görüntüle</Link>
                    )}
                    <button data-testid="save-draft" onClick={() => save()} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-bold disabled:opacity-50">
                        <Save className="h-3.5 w-3.5" /> Kaydet
                    </button>
                    {page.status !== "published" ? (
                        <button data-testid="publish-btn" onClick={publish} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold disabled:opacity-50">
                            <Eye className="h-3.5 w-3.5" /> Yayınla
                        </button>
                    ) : (
                        <button data-testid="unpublish-btn" onClick={draft} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold disabled:opacity-50">
                            Taslağa Al
                        </button>
                    )}
                </div>
            }
        >
            {/* View toggle */}
            <div className="inline-flex items-center rounded-xl bg-slate-100 dark:bg-slate-800 p-0.5 mb-4">
                <button onClick={() => setView("blocks")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === "blocks" ? "bg-white dark:bg-slate-900 text-sky-600 shadow-sm" : "text-slate-600 dark:text-slate-300"}`}>
                    <Layers className="h-3.5 w-3.5" /> Bloklar
                </button>
                <button onClick={() => setView("settings")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === "settings" ? "bg-white dark:bg-slate-900 text-sky-600 shadow-sm" : "text-slate-600 dark:text-slate-300"}`}>
                    <Settings className="h-3.5 w-3.5" /> Sayfa Ayarları
                </button>
            </div>

            {view === "settings" ? (
                <PageSettingsPanel page={page} setPage={setPage} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* LEFT: Blocks list */}
                    <div className="lg:col-span-3" data-testid="blocks-sidebar">
                        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 sticky top-24">
                            <h3 className="font-display text-sm font-bold text-slate-900 dark:text-white mb-3">Bloklar ({(page.blocks || []).length})</h3>
                            <div className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="block-list">
                                {(page.blocks || []).length === 0 && <div className="text-xs italic text-slate-400 py-3 text-center">Henüz blok yok.</div>}
                                {(page.blocks || []).map((b, idx, arr) => (
                                    <div key={b.id} data-testid={`block-item-${b.id}`}
                                        onClick={() => setSelectedBlockId(b.id)}
                                        className={`p-2 rounded-lg cursor-pointer text-xs transition-colors ${selectedBlockId === b.id ? "bg-sky-500 text-white" : "bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-700/60 text-slate-700 dark:text-slate-200"}`}>
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="font-semibold truncate">{idx + 1}. {BLOCK_TYPE_LABELS[b.type] || b.type}</span>
                                            <div className="flex items-center gap-0.5 flex-shrink-0">
                                                <button onClick={(e) => { e.stopPropagation(); moveBlock(b.id, -1); }} disabled={idx === 0} className="p-0.5 rounded hover:bg-white/20 disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
                                                <button onClick={(e) => { e.stopPropagation(); moveBlock(b.id, +1); }} disabled={idx === arr.length - 1} className="p-0.5 rounded hover:bg-white/20 disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
                                                <button onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }} className="p-0.5 rounded hover:bg-red-500 hover:text-white"><Trash2 className="h-3 w-3" /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">+ Yeni Blok</div>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {Object.keys(BLOCK_TYPE_LABELS).map((type) => (
                                        <button key={type} data-testid={`add-block-${type}`} onClick={() => addBlock(type)}
                                            className="px-2 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-950/40 text-[11px] font-semibold text-slate-700 dark:text-slate-200 transition-colors text-left">
                                            {BLOCK_TYPE_LABELS[type]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CENTER: live preview */}
                    <div className="lg:col-span-6">
                        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/40">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Canlı Önizleme</div>
                                <div className="text-[10px] font-mono text-slate-500">/p/{page.slug}</div>
                            </div>
                            <div className="max-h-[700px] overflow-y-auto bg-white dark:bg-slate-950" data-testid="page-preview">
                                {(page.blocks || []).length === 0 ? (
                                    <div className="text-center py-20 text-slate-400 text-sm">
                                        Sol panelden blok ekleyerek sayfanızı oluşturmaya başlayın.
                                    </div>
                                ) : (
                                    <BlockRenderer blocks={page.blocks || []} />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: block props editor */}
                    <div className="lg:col-span-3">
                        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 sticky top-24">
                            {selectedBlock ? (
                                <BlockPropsEditor block={selectedBlock} onChange={(patch) => updateBlock(selectedBlock.id, patch)} />
                            ) : (
                                <div className="text-center text-xs text-slate-400 py-10">
                                    Sol panelden bir blok seçin.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

// ======================================================================
// Block props editor — switches UI based on block.type
// ======================================================================
function BlockPropsEditor({ block, onChange }) {
    const p = block.props || {};
    const set = (k, v) => onChange({ [k]: v });

    return (
        <div className="space-y-3" data-testid="block-props-editor">
            <h3 className="font-display text-sm font-bold text-slate-900 dark:text-white pb-2 border-b border-slate-200 dark:border-slate-700">
                {BLOCK_TYPE_LABELS[block.type]} Ayarları
            </h3>

            {block.type === "hero" && <>
                <Field label="Üst etiket (eyebrow)"><input value={p.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className={INPUT} /></Field>
                <Field label="Başlık"><input data-testid="hero-title-input" value={p.title || ""} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                <Field label="Alt başlık"><textarea rows={2} value={p.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={INPUT} /></Field>
                <ImageField label="Arka plan görseli URL" value={p.bg_image_url} onChange={(v) => set("bg_image_url", v)} />
                <Field label="Overlay opacity (0-1)"><input type="number" step="0.1" min="0" max="1" value={p.overlay_opacity ?? 0.5} onChange={(e) => set("overlay_opacity", parseFloat(e.target.value))} className={INPUT} /></Field>
                <Field label="Hizalama">
                    <select value={p.align || "center"} onChange={(e) => set("align", e.target.value)} className={INPUT}>
                        <option value="left">Sol</option><option value="center">Orta</option><option value="right">Sağ</option>
                    </select>
                </Field>
                <Field label="CTA buton metni"><input value={p.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className={INPUT} /></Field>
                <Field label="CTA URL"><input value={p.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} className={INPUT} /></Field>
            </>}

            {block.type === "text" && <>
                <Field label="İçerik (HTML destekli)"><textarea rows={12} value={p.content_html || ""} onChange={(e) => set("content_html", e.target.value)} className={`${INPUT} font-mono text-xs`} /></Field>
                <p className="text-[10px] text-slate-500">İpucu: &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;a href=...&gt;, &lt;strong&gt;, &lt;em&gt; kullanabilirsiniz.</p>
            </>}

            {block.type === "image" && <>
                <ImageField label="Görsel URL" value={p.url} onChange={(v) => set("url", v)} />
                <Field label="Alt metni"><input value={p.alt || ""} onChange={(e) => set("alt", e.target.value)} className={INPUT} /></Field>
                <Field label="Açıklama (caption)"><input value={p.caption || ""} onChange={(e) => set("caption", e.target.value)} className={INPUT} /></Field>
                <Field label="Tıklanınca gidecek URL (opsiyonel)"><input value={p.link || ""} onChange={(e) => set("link", e.target.value)} className={INPUT} /></Field>
                <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!p.full_width} onChange={(e) => set("full_width", e.target.checked)} className="h-3.5 w-3.5 accent-sky-500" /> Tam genişlik</label>
            </>}

            {block.type === "gallery" && <>
                <Field label="Galeri başlığı"><input value={p.title || ""} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                <Field label="Sütun sayısı"><input type="number" min="2" max="6" value={p.columns || 3} onChange={(e) => set("columns", parseInt(e.target.value, 10))} className={INPUT} /></Field>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Görseller</div>
                    <div className="space-y-1.5">
                        {(p.images || []).map((img, i) => (
                            <div key={i} className="flex gap-1">
                                <input value={img.url} onChange={(e) => { const arr = [...p.images]; arr[i] = { ...arr[i], url: e.target.value }; set("images", arr); }} placeholder="https://..." className={`${INPUT} flex-1 text-xs font-mono`} />
                                <button onClick={() => set("images", p.images.filter((_, j) => j !== i))} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        ))}
                        <button onClick={() => set("images", [...(p.images || []), { url: "" }])} className="text-xs font-semibold text-sky-600 hover:underline">+ Görsel Ekle</button>
                    </div>
                </div>
            </>}

            {block.type === "cta" && <>
                <Field label="Başlık"><input value={p.title || ""} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                <Field label="Alt başlık"><textarea rows={2} value={p.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={INPUT} /></Field>
                <Field label="Birincil buton metni"><input value={p.primary_label || ""} onChange={(e) => set("primary_label", e.target.value)} className={INPUT} /></Field>
                <Field label="Birincil URL"><input value={p.primary_url || ""} onChange={(e) => set("primary_url", e.target.value)} className={INPUT} /></Field>
                <Field label="İkincil buton metni"><input value={p.secondary_label || ""} onChange={(e) => set("secondary_label", e.target.value)} className={INPUT} /></Field>
                <Field label="İkincil URL"><input value={p.secondary_url || ""} onChange={(e) => set("secondary_url", e.target.value)} className={INPUT} /></Field>
                <Field label="Arka plan (CSS)"><input value={p.bg_color || ""} onChange={(e) => set("bg_color", e.target.value)} placeholder="linear-gradient(...) veya #abcdef" className={`${INPUT} text-xs font-mono`} /></Field>
            </>}

            {block.type === "faq" && <>
                <Field label="Başlık"><input value={p.title || ""} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">SSS Öğeleri</div>
                    <div className="space-y-2">
                        {(p.items || []).map((it, i) => (
                            <div key={i} className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-1.5">
                                <input value={it.question} onChange={(e) => { const arr = [...p.items]; arr[i] = { ...arr[i], question: e.target.value }; set("items", arr); }} placeholder="Soru" className={INPUT} />
                                <textarea rows={2} value={it.answer} onChange={(e) => { const arr = [...p.items]; arr[i] = { ...arr[i], answer: e.target.value }; set("items", arr); }} placeholder="Cevap" className={INPUT} />
                                <button onClick={() => set("items", p.items.filter((_, j) => j !== i))} className="text-xs font-semibold text-red-600 hover:underline">Sil</button>
                            </div>
                        ))}
                        <button onClick={() => set("items", [...(p.items || []), { question: "", answer: "" }])} className="text-xs font-semibold text-sky-600 hover:underline">+ Soru Ekle</button>
                    </div>
                </div>
            </>}

            {block.type === "embed_video" && <>
                <Field label="Video başlığı (opsiyonel)"><input value={p.title || ""} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                <Field label="YouTube URL veya ID"><input value={p.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/watch?v=..." className={INPUT} /></Field>
            </>}

            {block.type === "custom_html" && <>
                <Field label="HTML"><textarea rows={12} value={p.html || ""} onChange={(e) => set("html", e.target.value)} className={`${INPUT} font-mono text-xs`} /></Field>
                <p className="text-[10px] text-amber-600">⚠️ Yalnızca güvendiğiniz HTML/CSS girin.</p>
            </>}

            {block.type === "hotel_list" && <>
                <Field label="Başlık"><input value={p.title || ""} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                <Field label="Kaynak">
                    <select value={p.source || "featured"} onChange={(e) => set("source", e.target.value)} className={INPUT}>
                        <option value="featured">Öne Çıkanlar</option>
                        <option value="deals">Fırsatlar</option>
                        <option value="destination">Belirli Destinasyon</option>
                    </select>
                </Field>
                {p.source === "destination" && (
                    <Field label="Destinasyon slug (örn. antalya)"><input value={p.destination_slug || ""} onChange={(e) => set("destination_slug", e.target.value)} className={INPUT} /></Field>
                )}
                <Field label="Adet"><input type="number" min="1" max="20" value={p.limit || 6} onChange={(e) => set("limit", parseInt(e.target.value, 10))} className={INPUT} /></Field>
                <Field label="Sütun sayısı"><input type="number" min="2" max="4" value={p.columns || 3} onChange={(e) => set("columns", parseInt(e.target.value, 10))} className={INPUT} /></Field>
            </>}

            {block.type === "spacer" && <>
                <Field label="Yükseklik (örn. 60px veya 4rem)"><input value={p.height || "60px"} onChange={(e) => set("height", e.target.value)} className={INPUT} /></Field>
            </>}
        </div>
    );
}

// ======================================================================
// Page Settings Panel — title, slug, status, scheduled dates, SEO, footer/header flags
// ======================================================================
function PageSettingsPanel({ page, setPage }) {
    const seo = page.seo || {};
    const setSeo = (k, v) => setPage({ ...page, seo: { ...seo, [k]: v } });
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* General */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3" data-testid="page-settings-general">
                <h3 className="font-display text-base font-bold text-slate-900 dark:text-white">Genel</h3>
                <Field label="Sayfa başlığı"><input data-testid="page-title" value={page.title || ""} onChange={(e) => setPage({ ...page, title: e.target.value })} className={INPUT} /></Field>
                <Field label="URL Slug"><input data-testid="page-slug" value={page.slug || ""} onChange={(e) => setPage({ ...page, slug: e.target.value })} className={`${INPUT} font-mono`} /></Field>
                <Field label="Durum">
                    <select data-testid="page-status" value={page.status || "draft"} onChange={(e) => setPage({ ...page, status: e.target.value })} className={INPUT}>
                        <option value="draft">Taslak</option>
                        <option value="published">Yayında</option>
                        <option value="scheduled">Zamanlı Yayın</option>
                    </select>
                </Field>
                {page.status === "scheduled" && (
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Yayın başlangıcı"><input type="datetime-local" value={page.publish_at ? page.publish_at.slice(0, 16) : ""} onChange={(e) => setPage({ ...page, publish_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className={INPUT} /></Field>
                        <Field label="Yayın bitişi (ops.)"><input type="datetime-local" value={page.unpublish_at ? page.unpublish_at.slice(0, 16) : ""} onChange={(e) => setPage({ ...page, unpublish_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className={INPUT} /></Field>
                    </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="show-header" checked={!!page.show_in_header} onChange={(e) => setPage({ ...page, show_in_header: e.target.checked })} className="h-4 w-4 accent-sky-500" /> Üst menüde göster</label>
                <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="show-footer" checked={!!page.show_in_footer} onChange={(e) => setPage({ ...page, show_in_footer: e.target.checked })} className="h-4 w-4 accent-sky-500" /> {"Footer'da göster"}</label>
            </div>

            {/* SEO */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3" data-testid="page-settings-seo">
                <h3 className="font-display text-base font-bold text-slate-900 dark:text-white">SEO</h3>
                <Field label="Meta Title"><input value={seo.title || ""} onChange={(e) => setSeo("title", e.target.value)} className={INPUT} /></Field>
                <Field label="Meta Description"><textarea rows={3} value={seo.description || ""} onChange={(e) => setSeo("description", e.target.value)} className={INPUT} /></Field>
                <Field label="Robots"><select value={seo.robots || "index,follow"} onChange={(e) => setSeo("robots", e.target.value)} className={INPUT}>
                    <option value="index,follow">index, follow (varsayılan)</option>
                    <option value="noindex,follow">noindex, follow</option>
                    <option value="noindex,nofollow">noindex, nofollow</option>
                </select></Field>
                <Field label="Canonical URL (ops.)"><input value={seo.canonical || ""} onChange={(e) => setSeo("canonical", e.target.value)} className={`${INPUT} font-mono text-xs`} /></Field>
                <ImageField label="OG Görsel URL" value={seo.og_image_url} onChange={(v) => setSeo("og_image_url", v)} />
            </div>
        </div>
    );
}

// ======================================================================
// Small reusable inputs
// ======================================================================
function Field({ label, children }) {
    return <label className="block"><span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>{children}</label>;
}

function ImageField({ label, value, onChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(r.data.url);
        } catch (e) {
            alert(e.response?.data?.detail || "Yükleme başarısız");
        } finally { setUploading(false); }
    };
    return (
        <div>
            {label && <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>}
            <div className="flex gap-2">
                <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className={`${INPUT} flex-1 text-xs font-mono`} />
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="px-3 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold disabled:opacity-50 whitespace-nowrap">
                    {uploading ? "..." : "Yükle"}
                </button>
                <input ref={inputRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
            </div>
            {value && <img src={resolveImageUrl(value)} alt="" className="mt-2 w-full h-24 object-cover rounded-lg" />}
        </div>
    );
}
